import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Button,
  Spinner,
  Flex,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import useDebounce from "../../hooks/useDebounce";
import { CiSearch } from "react-icons/ci";
import { IoCheckmarkOutline } from "react-icons/io5";
import { normalize } from "../../utils/utils";
import { LimitDataSelect } from "../../utils/globalVariable";

const SearchableSelect = ({
  options,
  initialSelectedOption,
  initialSelectedOptionParam,
  isShowSearch,
  setValue,
  width,
  minW,
  limitData,
  selectedKey,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialSelectedOption || initialSelectedOptionParam);
  const litmitGetData = limitData || LimitDataSelect;
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // console.log(options);
  useEffect(() => {
    if (debouncedSearchTerm) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (initialSelectedOption && initialSelectedOptionParam) {
      setSelectedOption(initialSelectedOption);
    }
  }, [initialSelectedOption]);

  // Controlled mode: khi truyền selectedKey, đồng bộ hiển thị theo state cha
  // (tránh mất lựa chọn khi component bị remount, ví dụ trong Skeleton lúc refetch)
  useEffect(() => {
    if (selectedKey === undefined) return;
    const match = options.find(
      (o) => String(o?.key) === String(selectedKey),
    );
    const next = match || initialSelectedOptionParam || initialSelectedOption;
    setSelectedOption((prev) =>
      String(prev?.key) === String(next?.key) ? prev : next,
    );
  }, [selectedKey, options]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      const name = normalize(option?.value); // fullName
      const code = normalize(option?.code); // code nhân viên
      const phone = normalize(option?.phone); // sđt nhân viên
      const identificationNumber = normalize(option?.identificationNumber); // cccd nhân viên
      const searchTerms = normalize(debouncedSearchTerm);

      return name.includes(searchTerms) || code.includes(searchTerms) || phone.includes(searchTerms) || identificationNumber.includes(searchTerms);
    });
  }, [debouncedSearchTerm, options]);

  // Kiểm tra có item thực sự được chọn (khác placeholder)
  const initialKey = (initialSelectedOptionParam || initialSelectedOption)?.key;
  const hasSelection = Boolean(selectedOption?.key) && selectedOption?.key !== initialKey;

  // Xoá selection — stopPropagation để không mở dropdown
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      const resetOption = initialSelectedOptionParam || initialSelectedOption;
      setSelectedOption(resetOption);
      setValue?.(resetOption?.key);
      setSearchTerm("");
    },
    [initialSelectedOption, initialSelectedOptionParam, setValue],
  );

  return (
    <Box
      w={width ? width : "auto"}
      maxW={width ? width : "250px"}
      minW={minW ? minW : "auto"}
      borderRadius={"12px"}
      height={"44px"}
    >
      <Menu matchWidth>
        <MenuButton
          _active={{
            border: "1px solid var(--color-option-employee-hover)",
          }}
          _hover={{
            boxShadow: "none",
          }}
          h={"44px"}
          as={Button}
          rightIcon={
            hasSelection ? (
              <Box
                as="span"
                onClick={handleClear}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="18px"
                h="18px"
                borderRadius="full"
                bg="var(--bg-line-employee)"
                cursor="pointer"
                flexShrink={0}
                _hover={{ opacity: 0.7 }}
              >
                <CloseIcon fontSize="7px" color="#666" />
              </Box>
            ) : (
              <ChevronDownIcon color={"#000"} />
            )
          }
          w="100%"
          backgroundColor="#FFF"
          border={"1px solid var(--Line)"}
        >
          <Text
            textAlign={"start"}
            color={selectedOption?.key ? "#000" : "var(--Text-color-Disable)"}
            fontSize={"14px"}
            fontWeight={500}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {selectedOption?.value}
          </Text>
        </MenuButton>
        <MenuList
          borderRadius={{ base: "12px" }}
          maxH={"250px"}
          overflow={"auto"}
          minW={"275px"}
        >
          {isShowSearch && (
            <Flex
              alignItems={"center"}
              gap={{ base: "12px" }}
              pl={"16px"}
              pr={"16px"}
              borderBottom={"0.5px solid var(--bg-line-employee)"}
            >
              <CiSearch color="var(--Text-color-Disable)" fontSize={"20px"} />
              <Input
                p={"0px"}
                fontSize={{ base: "14px" }}
                fontWeight={500}
                color="black"
                _placeholder={{ color: "var(--Text-color-Disable)" }}
                border={"none"}
                _focus={{ boxShadow: "none" }}
                placeholder="Tìm kiếm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {loading && (
                <Flex justify="center" mt={2}>
                  <Spinner size="sm" />
                </Flex>
              )}
            </Flex>
          )}
          {!loading &&
            filteredOptions.map((option, index) => (
              index <= litmitGetData && <Flex key={index}>
                <MenuItem
                  pl={"16px"}
                  onClick={() => {
                    if (selectedOption?.key === option?.key) {
                      setSelectedOption(initialSelectedOptionParam || initialSelectedOption);
                      setValue && initialSelectedOptionParam && setValue(initialSelectedOptionParam?.key);
                      setValue && !initialSelectedOptionParam && setValue(initialSelectedOption?.key);
                    } else {
                      setSelectedOption(option);
                      setValue && setValue(option?.key);
                    }
                    setSearchTerm("");
                  }}
                  backgroundColor={
                    selectedOption?.key === option?.key ? "#F9FAFD" : "#FFF"
                  }
                  color={
                    selectedOption?.key === option?.key ? "#1A9ED0" : "#000"
                  }
                >
                  <Flex alignItems="center" gap="8px">
                    <Text minW={"max-content"}>{option?.value}</Text>{" "}
                  </Flex>
                  <IoCheckmarkOutline
                    style={{
                      marginLeft: "auto",
                      visibility:
                        selectedOption?.key === option?.key
                          ? "visible"
                          : "hidden",
                    }}
                  />
                </MenuItem>
              </Flex>
            ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default SearchableSelect;
